/**
 * Remote device terminal, over the `shell` data channel.
 *
 * The phone holds shell UID 2000 through its own adb link and bridges this channel to an
 * interactive PTY (see ShellBridge.kt), so this is a real terminal: the working directory
 * persists, Ctrl-C works, and full-screen programs draw correctly.
 *
 * Wire format, matching the phone side:
 * - **Binary** both ways is raw terminal bytes. Keystrokes go up, output comes down. Raw
 *   because a PTY carries escape sequences and multi-byte UTF-8 that must survive intact.
 * - **Text** down is a JSON status frame (`ready`, `exited`, `error`); text up is a control
 *   frame, currently only `{"t":"resize","rows":R,"cols":C}`.
 */

import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';

interface ShellStatus {
  t: 'ready' | 'exited' | 'error';
  message?: string;
}

export class DeviceTerminal {
  private readonly term: Terminal;
  private readonly fit = new FitAddon();
  private readonly encoder = new TextEncoder();
  private channel: RTCDataChannel | null = null;
  private observer: ResizeObserver | null = null;
  private lastSize = { rows: 0, cols: 0 };

  constructor(private readonly container: HTMLElement) {
    this.term = new Terminal({
      convertEol: false, // the PTY already sends CRLF
      cursorBlink: true,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: 13,
      scrollback: 5000,
      theme: { background: '#0b0e14', foreground: '#c5c8c6' },
    });
    this.term.loadAddon(this.fit);
  }

  /** Attaches to an open `shell` channel and starts rendering into the container. */
  attach(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.binaryType = 'arraybuffer';

    if (!this.container.hasChildNodes()) {
      this.term.open(this.container);
    }

    // Keystrokes straight through as raw bytes, so control characters (Ctrl-C, Tab,
    // arrow-key escape sequences) reach the PTY unaltered.
    this.term.onData((data) => this.send(data));

    channel.onmessage = (event) => {
      if (typeof event.data === 'string') {
        this.handleStatus(event.data);
      } else {
        this.term.write(new Uint8Array(event.data as ArrayBuffer));
      }
    };
    channel.onclose = () => this.term.writeln('\r\n\x1b[33m[terminal disconnected]\x1b[0m');

    // Fit once the element has been laid out, then whenever it changes size.
    requestAnimationFrame(() => this.resize());
    this.observer = new ResizeObserver(() => this.resize());
    this.observer.observe(this.container);
  }

  /** Refits to the container and tells the phone the new geometry. */
  resize(): void {
    if (this.container.clientWidth === 0 || this.container.clientHeight === 0) return;
    try {
      this.fit.fit();
    } catch {
      return; // container not measurable yet
    }
    const { rows, cols } = this.term;
    // A PTY defaults to 80x24, so send the real size; skip no-op updates because each one
    // costs an `stty` command in the remote shell.
    if (rows === this.lastSize.rows && cols === this.lastSize.cols) return;
    this.lastSize = { rows, cols };
    if (this.channel?.readyState === 'open') {
      this.channel.send(JSON.stringify({ t: 'resize', rows, cols }));
    }
  }

  focus(): void {
    this.term.focus();
  }

  dispose(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (this.channel) {
      this.channel.onmessage = null;
      this.channel.onclose = null;
      this.channel = null;
    }
    this.term.dispose();
  }

  private send(data: string): void {
    if (this.channel?.readyState !== 'open') return;
    this.channel.send(this.encoder.encode(data));
  }

  private handleStatus(text: string): void {
    let status: ShellStatus;
    try {
      status = JSON.parse(text) as ShellStatus;
    } catch {
      return;
    }
    switch (status.t) {
      case 'ready':
        this.resize(); // the PTY exists now, so the size can be applied
        break;
      case 'exited':
        this.term.writeln('\r\n\x1b[33m[shell exited]\x1b[0m');
        break;
      case 'error':
        this.term.writeln(`\r\n\x1b[31m[${status.message ?? 'shell unavailable'}]\x1b[0m`);
        break;
    }
  }
}
